import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import InfoSvg from "../../../../common/svgs/InfoSvg";

type Tone = "warning" | "danger";
// "attached" → floats just below the control, anchored to a field-relative
//   container (used by the text/date/phone inputs).
// "gap"      → floats into the fixed 16px gap between this field and the next,
//   hugging the field top and clearing the next field (used by the address
//   field, whose next sibling has an opaque background).
type Placement = "attached" | "gap";

interface FieldHintMessageProps {
  // "warning" → yellow icon + weak-grey text (info/incomplete hints).
  // "danger"  → red icon + red text (validation errors).
  tone?: Tone;
  placement?: Placement;
  children: React.ReactNode;
}

const placementStyles: Record<Placement, object> = {
  attached: { position: "absolute", bottom: "-15px", left: 0 },
  gap: { position: "absolute", top: "100%", left: 0, marginTop: "2px" },
};

// Single source of truth for the small helper/validation messages under the
// Biometrics-tab fields (icon + typography + spacing all consistent).
const FieldHintMessage: React.FC<FieldHintMessageProps> = ({
  tone = "warning",
  placement = "attached",
  children,
}) => {
  const theme = useTheme();
  const iconColor =
    tone === "danger" ? theme.colorTokens.D[70] : theme.colorTokens.W[80];
  const textColor =
    tone === "danger"
      ? theme.colorTokens.D[70]
      : theme.colors_ds.NEUTRAL_TEXT_WEAK;

  return (
    <Box
      sx={{
        fontFamily: "var(--font-Medium), sans-serif",
        fontSize: "10px",
        lineHeight: "14px",
        color: textColor,
        display: "flex",
        alignItems: "center",
        gap: "4px",
        whiteSpace: "nowrap",
        ...placementStyles[placement],
      }}
    >
      <InfoSvg color={iconColor} size={12} />
      {children}
    </Box>
  );
};

export default FieldHintMessage;
